import React, { useMemo, useState } from "react";
import { Table, Tag } from "antd";
import { formatEnrolleeName, isActiveStatus } from "../search/searchResultUtils";
import { EnroleeBiodataModal } from "../compoinents/enroleeData";
import { PolicyHolderModal } from "../compoinents/userData";
import { retainedSlotsText } from "../compoinents/RetainedSlotsNote";
import { ADMIN_REVIEW_MODAL_CLASS } from "../dashboard/utils";

function StatusTag({ active }) {
  return <Tag color={active ? "success" : "default"}>{active ? "Active" : "Inactive"}</Tag>;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

function getClientColumns() {
  return [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      render: (v) => v || "—",
    },
    {
      title: "Policy no",
      dataIndex: "policy_no",
      key: "policy_no",
      width: 120,
      ellipsis: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 120,
      ellipsis: true,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 100,
      render: (v) => (v ? String(v).toUpperCase() : "—"),
    },
    {
      title: "Package",
      key: "package",
      width: 100,
      ellipsis: true,
      render: (_, r) => r?.insurance_package?.toUpperCase?.() || "—",
    },
    {
      title: "Premium",
      key: "premium_type",
      width: 96,
      render: (_, r) => r?.premium_type?.toUpperCase?.() || "—",
    },
    {
      title: "Enrollees",
      key: "enrollees",
      width: 96,
      render: (_, r) => {
        // Headcount over plan size. When archived enrollees still hold seats the
        // pair reads as free capacity that does not exist — say so inline.
        const held = retainedSlotsText(r);
        return (
          <>
            {`${r?.active_enrollees ?? 0} / ${r?.enrolee_size ?? 0}`}
            {held ? <div style={{ fontSize: 11, opacity: 0.65 }}>{held}</div> : null}
          </>
        );
      },
    },
    {
      title: "Joined",
      dataIndex: "date",
      key: "date",
      width: 110,
      render: (v) => formatDate(v),
    },
    {
      title: "Status",
      key: "status",
      width: 88,
      fixed: "right",
      render: (_, r) => <StatusTag active={isActiveStatus(r?.status)} />,
    },
  ];
}

function getEnrolleeColumns() {
  return [
    {
      title: "Name",
      key: "name",
      ellipsis: true,
      render: (_, r) => formatEnrolleeName(r),
    },
    {
      title: "Insurance no",
      dataIndex: "insurance_no",
      key: "insurance_no",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Policy no",
      dataIndex: "policy_no",
      key: "policy_no",
      width: 120,
      ellipsis: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 120,
      ellipsis: true,
    },
    {
      title: "Location",
      key: "location",
      width: 140,
      ellipsis: true,
      render: (_, r) => [r?.city, r?.state].filter(Boolean).join(", ") || "—",
    },
    {
      title: "Registered",
      dataIndex: "date",
      key: "date",
      width: 110,
      render: (v) => formatDate(v),
    },
    {
      title: "Status",
      key: "status",
      width: 88,
      fixed: "right",
      render: (_, r) => <StatusTag active={isActiveStatus(r?.status)} />,
    },
  ];
}

export default function VerifyQueueTable({ data, isClientScope }) {
  const [clientRecord, setClientRecord] = useState(null);
  const [enrolleeRecord, setEnrolleeRecord] = useState(null);

  const columns = useMemo(
    () => (isClientScope ? getClientColumns() : getEnrolleeColumns()),
    [isClientScope],
  );

  const handleRowClick = (record) => {
    if (isClientScope) {
      setClientRecord(record);
    } else {
      setEnrolleeRecord(record);
    }
  };

  if (!Array.isArray(data) || data.length === 0) return null;

  return (
    <>
      <Table
        className="search-results-table verify-queue-table"
        size="small"
        rowKey={(record) => record.id ?? record.policy_no ?? record.insurance_no}
        columns={columns}
        dataSource={data}
        scroll={{ x: "max-content" }}
        pagination={false}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: "pointer" },
        })}
      />
      {isClientScope
        ? clientRecord && (
            <PolicyHolderModal
              data={clientRecord}
              display={!!clientRecord}
              action={() => setClientRecord(null)}
              onclose={() => setClientRecord(null)}
              showAllActions
              modalClassName={ADMIN_REVIEW_MODAL_CLASS}
            />
          )
        : enrolleeRecord && (
            <EnroleeBiodataModal
              data={enrolleeRecord}
              display={!!enrolleeRecord}
              action={() => setEnrolleeRecord(null)}
              modalClassName={ADMIN_REVIEW_MODAL_CLASS}
            />
          )}
    </>
  );
}
